import clsx from 'clsx';
import ReactSelect, { createFilter, components as reactSelectComponents } from 'react-select';
import CreatableSelect from 'react-select/creatable';
import type {
  ActionMeta,
  ClassNamesConfig,
  ContainerProps,
  FormatOptionLabelMeta,
  GroupBase,
  MultiValue as MV,
  SingleValue as SV,
} from 'react-select';
import type { Option, SelectProps } from './Select.types';

const baseClassNames = `
  tw:focus:ring-0
  tw:disabled:cursor-not-allowed
`;

const containerClassNames = `
  tw:bg-sq-white
  tw:dark:bg-sq-dark-background
`;

const errorClassNames = 'tw:border-sq-danger';

const borderColorClassNames = `
  tw:border-sq-disabled-gray
  tw:dark:border-sq-dark-disabled-gray
`;

const borderClassNames = `
  tw:border
  tw:border-solid
`;

const borderActiveStyles = `
  tw:dark:hover:border-sq-theme-darker
  tw:dark:focus-within:border-sq-theme-darker
  tw:dark:active:border-sq-theme-darker
  tw:focus-within:border-sq-theme-dark
  tw:hover:border-sq-theme-dark
  tw:active:border-sq-theme-dark
`;

const textClassNames = `
  tw:text-sm
  tw:text-sq-text-color
  tw:dark:text-sq-dark-text
`;

export type MultiValue<T> = MV<T>;
export type SingleValue<T> = SV<T>;

const textActiveStyles = `
  tw:dark:focus:text-sq-theme-darker
  tw:dark:active:text-sq-theme-darker
  tw:dark:hover:text-sq-theme-darker
  tw:hover:text-sq-theme-dark
  tw:focus:text-sq-theme-dark
  tw:active:text-sq-theme-dark
  tw:dark:text-sq-dark-disabled-gray
`;

const menuClassNames = `
  tw:z-[9999]!
  tw:min-w-fit
  tw:rounded-b
  tw:border
  tw:border-solid
  tw:border-sq-disabled-gray
  tw:whitespace-nowrap
  tw:dark:border-sq-dark-disabled-gray
`;

const menuListStyles = `
  tw:z-[9999]!
  tw:min-w-fit
  tw:rounded-b
  tw:bg-sq-white
  tw:dark:bg-sq-dark-background
  specSelectMenu
`;

const groupHeadingStyles = `
  tw:bg-sq-light-gray
  tw:px-2.5
  tw:py-1
  tw:text-sq-darkish-gray
  tw:dark:bg-sq-dark-disabled-gray
  specSelectGroupHeading
`;

const optionStyles = `
  tw:px-2.5
  tw:py-1
  tw:hover:cursor-pointer
  tw:hover:bg-sq-gray-highlight
  tw:dark:hover:bg-sq-gray-highlight-dark
  specSelectOption
`;

const disabledFieldClassNames = `
  tw:cursor-not-allowed!
  tw:bg-sq-light-gray
  tw:text-sq-darkish-gray
  tw:dark:bg-sq-dark-disabled-gray
  tw:dark:text-sq-dark-text/30
`;

const placeholderStyles = `
  tw:text-gray-400
  tw:dark:text-sq-dark-text-lighter
  specOpenSelect
`;

const multiValueStyles = `
  tw:m-0.5
  tw:rounded-sm
  tw:bg-sq-disabled-gray
  tw:px-1
  tw:py-0.5
  tw:text-sm
  tw:dark:bg-sq-multi-gray-dark
  specOpenSelect
`;

const controlHeights = {
  small: 'reactSelectMinHeightSmall tw:pr-1 tw:pl-2',
  regular: 'reactSelectMinHeight tw:pr-1.5 tw:pl-2.5',
};

const selectControlStyle = 'specSelectControl';
const selectInputStyle = 'specSelectInput';
const disabledOptionStyle = 'specDisabledOption';
const openIndicatorStyle = 'specOpenIt';

/**
 * react-select does not render unknown props onto the DOM, but it does forward them to selectProps, so this
 * container override reads the testId from there. It must be defined at module level: react-select remounts any
 * slot whose component identity changes between renders, which drops input focus and closes an open menu.
 */
const SelectContainerWithTestId = (containerProps: ContainerProps<Option, boolean, GroupBase<Option>>) => (
  <reactSelectComponents.SelectContainer
    {...containerProps}
    innerProps={
      {
        ...containerProps.innerProps,
        'data-testid': (containerProps.selectProps as { testId?: string }).testId,
      } as typeof containerProps.innerProps
    }
  />
);

/**
 * Select Component
 *  - based on react-select (https://react-select.com/)
 *
 *  For ease of testing most of the elements of this select can be identified by classNames prefixed with "spec".
 *
 */
const Select = ({
  options,
  value,
  placeholder = 'select',
  noOptionsMessage = 'no matching options',
  isSearchable = true,
  creatable = false,
  isMulti = false,
  isClearable = false,
  closeMenuOnSelect = true,
  id,
  inputId,
  menuPortalTarget,
  getOptionLabel,
  getSelectedValueLabel,
  getOptionValue,
  onChange,
  menuIsOpen,
  menuPlacement = 'auto',
  extraClassNames,
  controlClassNames = '',
  inputGroup,
  filterConfig,
  filterOption,
  small = false,
  isDisabled = false,
  showError = false,
  isLoading = false,
  formatGroupLabel,
  onRemove,
  defaultValue,
  onMenuOpen,
  onMenuClose,
  components: componentsProp,
  onMenuInputFocus,
  onInputChange,
  inputValue,
  onMenuScrollToBottom,
  testId,
}: SelectProps) => {
  // A consumer-provided SelectContainer wins over the testId-injecting one.
  const components = { SelectContainer: SelectContainerWithTestId, ...componentsProp };

  const getOptionOrSelectedLabel = (option: Option, { context }: FormatOptionLabelMeta<Option>) => {
    const getSelectedOptionLabel = getSelectedValueLabel ? getSelectedValueLabel : getOptionLabel;

    if (getOptionLabel || getSelectedValueLabel) {
      if (context === 'value') {
        return getSelectedOptionLabel ? getSelectedOptionLabel(option) : option.label;
      } else {
        return getOptionLabel ? getOptionLabel(option) : option.label;
      }
    } else {
      return option.label;
    }
  };

  // oxlint-disable-next-line typescript/no-redundant-type-constituents
  const interceptOnChange = (newValue: MultiValue<Option> | SingleValue<Option>, actionMeta: ActionMeta<Option>) => {
    if (isMulti && onRemove && actionMeta.action === 'remove-value') {
      onRemove({ id: actionMeta.removedValue });
      return;
    }
    onChange(newValue);
  };

  // react-select defaults blurInputOnSelect to true on touch-capable devices; that can close the menu even when
  // closeMenuOnSelect is false. Only make `blurInputOnSelect=false` when `closeMenuOnSelect=false` (e.g., multi-select
  // where the menu should stay open after each pick), otherwise set to `undefined` to default to the built-in
  // react-select behaviour.
  const blurInputOnSelect = closeMenuOnSelect ? undefined : false;

  const props = {
    id,
    value,
    inputId,
    placeholder,
    closeMenuOnSelect,
    blurInputOnSelect,
    isSearchable: creatable ? true : isSearchable,
    isMulti,
    isClearable,
    isLoading,
    defaultValue,
    noOptionsMessage: () => noOptionsMessage,
    menuPortalTarget,
    menuIsOpen,
    onChange: interceptOnChange,
    unstyled: true,
    menuPlacement,
    formatOptionLabel: getOptionOrSelectedLabel,
    formatGroupLabel,
    getOptionValue,
    options,
    isDisabled,
    onMenuOpen,
    onMenuClose,
    components,
    // Not a react-select prop; forwarded through selectProps for SelectContainerWithTestId to read.
    testId,
    onMenuInputFocus,
    onInputChange,
    inputValue,
    onMenuScrollToBottom,
    // react-select only fires onMenuScrollToBottom when it captures menu scroll, and captureMenuScroll defaults to
    // false on touch-capable environments — which includes jsdom, which means it would never be triggered. We force it
    // on in tests only; real browsers keep the react-select default.
    // oxlint-disable-next-line no-undef
    captureMenuScroll: process.env.NODE_ENV === 'test' ? true : undefined,
    hideSelectedOptions: isMulti,
    filterOption: filterOption ? filterOption : filterConfig ? createFilter(filterConfig) : undefined,
    classNames: {
      control: ({ menuIsOpen }) => {
        let border = menuIsOpen ? 'tw:rounded-t-md' : 'tw:rounded-md';

        // if it's on the left side of the input group make sure the right side is straight and not curved
        if (inputGroup === 'left') {
          border = menuIsOpen ? 'tw:rounded-tl-md' : 'tw:rounded-l-md';
        } else if (inputGroup === 'right') {
          border = menuIsOpen ? 'tw:rounded-tr-md' : 'tw:rounded-r-md';
        }
        return clsx(
          borderClassNames,
          !isDisabled && borderActiveStyles,
          border,
          showError ? errorClassNames : borderColorClassNames,
          small ? controlHeights.small : controlHeights.regular,
          controlClassNames,
          baseClassNames,
          !isDisabled && containerClassNames,
          selectControlStyle,
          isDisabled && disabledFieldClassNames,
        );
      },
      placeholder: () => placeholderStyles,
      container: () => clsx(textClassNames, extraClassNames, 'tw:pointer-events-auto!'),
      input: () => clsx(textClassNames, selectInputStyle),
      menuList: () => menuListStyles,
      menu: () => menuClassNames,
      menuPortal: () => 'tw:z-[9000]!',
      dropdownIndicator: () => clsx('tw:text-sq-disabled-gray', textActiveStyles, openIndicatorStyle),
      option: ({ isSelected, isDisabled, isFocused }) => {
        let classNames = optionStyles;
        if (isDisabled) {
          classNames = clsx(classNames, disabledOptionStyle);
        }
        if (isSelected) {
          return clsx(classNames, 'tw:bg-sq-colored-hover tw:dark:bg-sq-colored-hover-dark');
        }
        return isFocused ? clsx(classNames, 'tw:bg-sq-gray-highlight tw:dark:bg-sq-gray-highlight-dark') : classNames;
      },
      singleValue: () => 'specOpenSelect',
      multiValue: () => multiValueStyles,
      multiValueRemove: () => 'tw:hover:text-sq-danger specSelectMultiValueRemove',
      clearIndicator: () => 'tw:text-sq-disabled-gray tw:hover:text-sq-danger specClearSelect',
      group: () => 'specSelectGroup',
      groupHeading: () => groupHeadingStyles,
    } satisfies ClassNamesConfig<Option, boolean, GroupBase<Option>>,
  };

  return creatable ? <CreatableSelect {...props} /> : <ReactSelect {...props} />;
};
export default Select;
